import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { cacheExhibit, cacheLanguages, cacheRoom, sendMessageToSW } from '../sw/register';

// Define the types for our sync context
type SyncAction = {
  id: string;
  type: string;
  data: any;
  timestamp: number;
  retries?: number;
};

type SyncState = {
  isOnline: boolean;
  pendingActions: SyncAction[];
  syncInProgress: boolean;
  lastSyncTime: number | null;
};

type SyncContextType = SyncState & {
  addSyncAction: (type: string, data: any) => void;
  removeSyncAction: (id: string) => void;
  processSyncQueue: () => Promise<void>;
  cacheExhibitData: (exhibitId: string) => void;
  cacheRoomData: (roomId: string) => void;
  cacheLanguageData: (languages: string[]) => void;
};

// Create the context with a default value
const SyncContext = createContext<SyncContextType | undefined>(undefined);

// Action types for our reducer
enum ActionTypes {
  SET_ONLINE_STATUS = 'SET_ONLINE_STATUS',
  ADD_SYNC_ACTION = 'ADD_SYNC_ACTION',
  REMOVE_SYNC_ACTION = 'REMOVE_SYNC_ACTION',
  SET_SYNC_IN_PROGRESS = 'SET_SYNC_IN_PROGRESS',
  SET_LAST_SYNC_TIME = 'SET_LAST_SYNC_TIME',
}

// Define the actions for our reducer
type SyncReducerAction =
  | { type: ActionTypes.SET_ONLINE_STATUS; payload: boolean }
  | { type: ActionTypes.ADD_SYNC_ACTION; payload: SyncAction }
  | { type: ActionTypes.REMOVE_SYNC_ACTION; payload: string }
  | { type: ActionTypes.SET_SYNC_IN_PROGRESS; payload: boolean }
  | { type: ActionTypes.SET_LAST_SYNC_TIME; payload: number };

// Initial state for our context
const initialState: SyncState = {
  isOnline: navigator.onLine,
  pendingActions: [],
  syncInProgress: false,
  lastSyncTime: null,
};

// Reducer to handle our sync state
const syncReducer = (state: SyncState, action: SyncReducerAction): SyncState => {
  switch (action.type) {
    case ActionTypes.SET_ONLINE_STATUS:
      return { ...state, isOnline: action.payload };
    case ActionTypes.ADD_SYNC_ACTION:
      return {
        ...state,
        pendingActions: [
          ...state.pendingActions.filter((item) => item.id !== action.payload.id),
          action.payload,
        ],
      };
    case ActionTypes.REMOVE_SYNC_ACTION:
      return {
        ...state,
        pendingActions: state.pendingActions.filter((item) => item.id !== action.payload),
      };
    case ActionTypes.SET_SYNC_IN_PROGRESS:
      return { ...state, syncInProgress: action.payload };
    case ActionTypes.SET_LAST_SYNC_TIME:
      return { ...state, lastSyncTime: action.payload };
    default:
      return state;
  }
};

// Get actions from local storage
const loadActionsFromStorage = (): SyncAction[] => {
  try {
    const storedActions = localStorage.getItem('pendingSyncActions');
    return storedActions ? JSON.parse(storedActions) : [];
  } catch (error) {
    console.error('Failed to load sync actions from storage:', error);
    return [];
  }
};

// Save actions to local storage
const saveActionsToStorage = (actions: SyncAction[]): void => {
  try {
    localStorage.setItem('pendingSyncActions', JSON.stringify(actions));
  } catch (error) {
    console.error('Failed to save sync actions to storage:', error);
  }
};

// Provider component
export const SyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state with actions from storage
  const [state, dispatch] = useReducer(syncReducer, {
    ...initialState,
    pendingActions: loadActionsFromStorage(),
  });

  // Save actions to storage whenever they change
  useEffect(() => {
    saveActionsToStorage(state.pendingActions);
  }, [state.pendingActions]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      dispatch({ type: ActionTypes.SET_ONLINE_STATUS, payload: true });
      // Try to sync when we come back online
      processSyncQueue();
    };

    const handleOffline = () => {
      dispatch({ type: ActionTypes.SET_ONLINE_STATUS, payload: false });
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Handle messages from service worker
    const handleSyncMessage = (event: Event) => {
      // When we receive a sync message from the service worker, process our queue
      processSyncQueue();
    };

    window.addEventListener('syncVisitorNotes', handleSyncMessage);
    window.addEventListener('syncAnalytics', handleSyncMessage);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('syncVisitorNotes', handleSyncMessage);
      window.removeEventListener('syncAnalytics', handleSyncMessage);
    };
  }, []);

  // Add a sync action to the queue
  const addSyncAction = (type: string, data: any) => {
    const action: SyncAction = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      retries: 0,
    };

    dispatch({ type: ActionTypes.ADD_SYNC_ACTION, payload: action });

    // If we're online, try to process immediately
    if (state.isOnline && !state.syncInProgress) {
      processSyncQueue();
    }
  };

  // Remove a sync action from the queue
  const removeSyncAction = (id: string) => {
    dispatch({ type: ActionTypes.REMOVE_SYNC_ACTION, payload: id });
  };

  // Process the sync queue
  const processSyncQueue = async (): Promise<void> => {
    // If we're offline or already syncing, do nothing
    if (!state.isOnline || state.syncInProgress || state.pendingActions.length === 0) {
      return;
    }

    dispatch({ type: ActionTypes.SET_SYNC_IN_PROGRESS, payload: true });

    try {
      // Process each action in the queue
      for (const action of state.pendingActions) {
        try {
          await processSyncAction(action);
          removeSyncAction(action.id);
        } catch (error) {
          console.error(`Failed to sync action ${action.type}:`, error);
          // Increment retry count
          const updatedAction = {
            ...action,
            retries: (action.retries ?? 0) + 1,
          };
          // If we've tried more than 5 times, give up
          if (updatedAction.retries > 5) {
            removeSyncAction(action.id);
          } else {
            dispatch({ type: ActionTypes.ADD_SYNC_ACTION, payload: updatedAction });
          }
        }
      }
    } finally {
      dispatch({ type: ActionTypes.SET_SYNC_IN_PROGRESS, payload: false });
      dispatch({ type: ActionTypes.SET_LAST_SYNC_TIME, payload: Date.now() });
    }
  };

  // Process a single sync action
  const processSyncAction = async (action: SyncAction): Promise<void> => {
    switch (action.type) {
      case 'SAVE_VISITOR_NOTE':
        await saveVisitorNote(action.data);
        break;
      case 'TRACK_ANALYTICS':
        await trackAnalytics(action.data);
        break;
      case 'CACHE_EXHIBIT':
        cacheExhibitData(action.data.exhibitId);
        break;
      case 'CACHE_ROOM':
        cacheRoomData(action.data.roomId);
        break;
      case 'CACHE_LANGUAGES':
        cacheLanguageData(action.data.languages);
        break;
      default:
        console.warn(`Unknown sync action type: ${action.type}`);
    }
  };

  // Save a visitor note to the server
  const saveVisitorNote = async (data: any): Promise<void> => {
    // Implement API call to save visitor note
    const response = await fetch('/api/v1/visitor-notes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to save visitor note: ${response.statusText}`);
    }
  };

  // Track analytics on the server
  const trackAnalytics = async (data: any): Promise<void> => {
    // Implement API call to track analytics
    const response = await fetch('/api/v1/analytics', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to track analytics: ${response.statusText}`);
    }
  };

  // Cache exhibit data
  const cacheExhibitData = (exhibitId: string): void => {
    cacheExhibit(exhibitId);
    console.log(`Requested caching for exhibit: ${exhibitId}`);
  };

  // Cache room data
  const cacheRoomData = (roomId: string): void => {
    cacheRoom(roomId);
    console.log(`Requested caching for room: ${roomId}`);
  };

  // Cache language data
  const cacheLanguageData = (languages: string[]): void => {
    cacheLanguages(languages);
    console.log(`Requested caching for languages: ${languages.join(', ')}`);
  };

  // Provide the context value
  const contextValue: SyncContextType = {
    ...state,
    addSyncAction,
    removeSyncAction,
    processSyncQueue,
    cacheExhibitData,
    cacheRoomData,
    cacheLanguageData,
  };

  return <SyncContext.Provider value={contextValue}>{children}</SyncContext.Provider>;
};

// Hook to use the sync context
export const useSync = (): SyncContextType => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
};
